import React, { useRef } from 'react';
import {
  Printer,
  Box,
  Layers,
  Package,
  PieChart,
  Edit3,
  Image as ImageIcon,
} from 'lucide-react';
import {
  calculateStatistics,
  calculateUsedUnits,
} from './qconfigurator.types';

const QConfiguratorSummary = ({
  modules,
  onEditModule,
}) => {
  const printRef = useRef(null);
  const stats = calculateStatistics(modules);

  // Handle print
  const handlePrint = () => {
    const printContent = printRef.current;
    if (!printContent) return;

    const printWindow = window.open('', '_blank');
    if (!printWindow) return;

    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Configurator - Inventory Report</title>
          <style>
            * { margin: 0; padding: 0; box-sizing: border-box; }
            body {
              font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
              padding: 20px;
              color: #18181b;
            }
            .print-header {
              text-align: center;
              margin-bottom: 30px;
              padding-bottom: 20px;
              border-bottom: 2px solid #dc2626;
            }
            .print-header h1 { font-size: 24px; color: #18181b; }
            .print-header p { color: #71717a; margin-top: 5px; }
            .stats-grid {
              display: grid;
              grid-template-columns: repeat(4, 1fr);
              gap: 15px;
              margin-bottom: 30px;
            }
            .stat-card {
              background: #fafafa;
              padding: 15px;
              border-radius: 8px;
              text-align: center;
            }
            .stat-value { font-size: 24px; font-weight: bold; color: #dc2626; }
            .stat-label { font-size: 12px; color: #71717a; margin-top: 5px; }
            .module-section {
              margin-bottom: 30px;
              page-break-inside: avoid;
            }
            .module-header {
              background: #18181b;
              color: white;
              padding: 12px 15px;
              border-radius: 8px 8px 0 0;
              display: flex;
              justify-content: space-between;
              align-items: center;
            }
            .module-header h3 { font-size: 16px; }
            .module-meta { font-size: 12px; opacity: 0.8; }
            .drawer-item {
              border: 1px solid #e4e4e7;
              border-top: none;
              padding: 15px;
            }
            .drawer-item:last-child { border-radius: 0 0 8px 8px; }
            .drawer-header {
              display: flex;
              justify-content: space-between;
              align-items: center;
              margin-bottom: 10px;
            }
            .drawer-name { font-weight: 600; }
            .drawer-size {
              background: #fef2f2;
              color: #dc2626;
              padding: 2px 8px;
              border-radius: 4px;
              font-size: 11px;
            }
            .drawer-image {
              width: 100%;
              max-width: 300px;
              border-radius: 8px;
              margin: 10px 0;
            }
            .items-list { margin-top: 10px; }
            .item-row {
              display: flex;
              align-items: flex-start;
              gap: 10px;
              padding: 8px 0;
              border-bottom: 1px solid #f4f4f5;
            }
            .item-row:last-child { border-bottom: none; }
            .item-image {
              width: 50px;
              height: 50px;
              object-fit: cover;
              border-radius: 4px;
            }
            .item-details { flex: 1; }
            .item-name { font-weight: 500; }
            .item-desc { font-size: 12px; color: #71717a; margin-top: 2px; }
            .item-qty {
              background: #f4f4f5;
              padding: 2px 8px;
              border-radius: 4px;
              font-size: 12px;
            }
            .no-items { color: #a1a1aa; font-style: italic; font-size: 13px; }
            .footer {
              margin-top: 40px;
              padding-top: 20px;
              border-top: 1px solid #e4e4e7;
              text-align: center;
              font-size: 11px;
              color: #a1a1aa;
            }
            @media print {
              body { padding: 0; }
              .module-section { page-break-inside: avoid; }
            }
          </style>
        </head>
        <body>
          ${printContent.innerHTML}
        </body>
      </html>
    `);

    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 250);
  };

  return (
    <div className="qconfig-summary">
      {/* Summary Header */}
      <div className="qconfig-summary-header">
        <div>
          <h2>Inventory Summary Report</h2>
          <p>Complete overview of your tool cabinet configuration</p>
        </div>
        <button onClick={handlePrint} className="qconfig-print-btn">
          <Printer className="w-5 h-5" />
          <span>Print Report</span>
        </button>
      </div>

      {/* Statistics Cards */}
      <div className="qconfig-stats-grid">
        <div className="qconfig-stat-card">
          <div className="qconfig-stat-icon">
            <Box className="w-6 h-6" />
          </div>
          <div className="qconfig-stat-value">{stats.totalModules}</div>
          <div className="qconfig-stat-label">Total Modules</div>
        </div>
        <div className="qconfig-stat-card">
          <div className="qconfig-stat-icon">
            <Layers className="w-6 h-6" />
          </div>
          <div className="qconfig-stat-value">{stats.totalDrawers}</div>
          <div className="qconfig-stat-label">Total Drawers</div>
        </div>
        <div className="qconfig-stat-card">
          <div className="qconfig-stat-icon">
            <Package className="w-6 h-6" />
          </div>
          <div className="qconfig-stat-value">{stats.totalItems}</div>
          <div className="qconfig-stat-label">Total Items</div>
        </div>
        <div className="qconfig-stat-card">
          <div className="qconfig-stat-icon">
            <PieChart className="w-6 h-6" />
          </div>
          <div className="qconfig-stat-value">
            {stats.totalCapacityAvailable > 0
              ? Math.round((stats.totalCapacityUsed / stats.totalCapacityAvailable) * 100)
              : 0}%
          </div>
          <div className="qconfig-stat-label">Capacity Used</div>
        </div>
      </div>

      {/* Drawer Distribution */}
      <div className="qconfig-distribution">
        <h3>Drawer Distribution</h3>
        <div className="qconfig-distribution-bars">
          <div className="qconfig-distribution-bar">
            <span className="label">Small</span>
            <div className="bar">
              <div
                className="fill small"
                style={{
                  width: stats.totalDrawers > 0
                    ? `${(stats.drawersBySize.small / stats.totalDrawers) * 100}%`
                    : '0%',
                }}
              />
            </div>
            <span className="count">{stats.drawersBySize.small}</span>
          </div>
          <div className="qconfig-distribution-bar">
            <span className="label">Medium</span>
            <div className="bar">
              <div
                className="fill medium"
                style={{
                  width: stats.totalDrawers > 0
                    ? `${(stats.drawersBySize.medium / stats.totalDrawers) * 100}%`
                    : '0%',
                }}
              />
            </div>
            <span className="count">{stats.drawersBySize.medium}</span>
          </div>
          <div className="qconfig-distribution-bar">
            <span className="label">Large</span>
            <div className="bar">
              <div
                className="fill large"
                style={{
                  width: stats.totalDrawers > 0
                    ? `${(stats.drawersBySize.large / stats.totalDrawers) * 100}%`
                    : '0%',
                }}
              />
            </div>
            <span className="count">{stats.drawersBySize.large}</span>
          </div>
        </div>
      </div>

      {/* Printable Content (Hidden on screen but used for printing) */}
      <div ref={printRef} className="qconfig-print-content">
        <div className="print-header">
          <h1>Configurator - Inventory Report</h1>
          <p>Generated on {new Date().toLocaleDateString()} at {new Date().toLocaleTimeString()}</p>
        </div>

        <div className="stats-grid">
          <div className="stat-card">
            <div className="stat-value">{stats.totalModules}</div>
            <div className="stat-label">Total Modules</div>
          </div>
          <div className="stat-card">
            <div className="stat-value">{stats.totalDrawers}</div>
            <div className="stat-label">Total Drawers</div>
          </div>
          <div className="stat-card">
            <div className="stat-value">{stats.totalItems}</div>
            <div className="stat-label">Total Items</div>
          </div>
          <div className="stat-card">
            <div className="stat-value">
              {stats.totalCapacityAvailable > 0
                ? Math.round((stats.totalCapacityUsed / stats.totalCapacityAvailable) * 100)
                : 0}%
            </div>
            <div className="stat-label">Capacity Used</div>
          </div>
        </div>

        {modules.map((module) => (
          <div key={module.id} className="module-section">
            <div className="module-header">
              <h3>{module.name}</h3>
              <span className="module-meta">
                {module.drawers.length} drawers &bull; {calculateUsedUnits(module.drawers)}/3 units
              </span>
            </div>

            {module.drawers.length === 0 ? (
              <div className="drawer-item">
                <p className="no-items">No drawers in this module</p>
              </div>
            ) : (
              module.drawers.map((drawer) => (
                <div key={drawer.id} className="drawer-item">
                  <div className="drawer-header">
                    <span className="drawer-name">{drawer.name}</span>
                    <span className="drawer-size">{drawer.size.toUpperCase()} ({drawer.units} units)</span>
                  </div>

                  {drawer.image && (
                    <img src={drawer.image} alt={drawer.name} className="drawer-image" />
                  )}

                  {drawer.items.length === 0 ? (
                    <p className="no-items">No items in this drawer</p>
                  ) : (
                    <div className="items-list">
                      {drawer.items.map((item) => (
                        <div key={item.id} className="item-row">
                          {item.image && (
                            <img src={item.image} alt={item.name} className="item-image" />
                          )}
                          <div className="item-details">
                            <div className="item-name">{item.name}</div>
                            {item.description && (
                              <div className="item-desc">{item.description}</div>
                            )}
                          </div>
                          <span className="item-qty">x{item.quantity}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        ))}

        <div className="footer">
          Configurator &bull; Tool Cabinet Configuration System &bull; Ventro.energy
        </div>
      </div>

      {/* On-Screen Module List */}
      <div className="qconfig-modules-overview">
        <h3>Modules Overview</h3>

        {modules.length === 0 ? (
          <div className="qconfig-no-data">
            <p>No modules configured yet</p>
          </div>
        ) : (
          <div className="qconfig-modules-cards">
            {modules.map((module) => (
              <div key={module.id} className="qconfig-module-overview-card">
                <div className="qconfig-module-overview-header">
                  <div
                    className="qconfig-module-color-dot"
                    style={{ backgroundColor: module.color }}
                  />
                  <h4>{module.name}</h4>
                  <button
                    onClick={() => onEditModule(module.id)}
                    className="qconfig-edit-module-btn"
                  >
                    <Edit3 className="w-4 h-4" />
                  </button>
                </div>

                <div className="qconfig-module-overview-stats">
                  <span>{module.drawers.length} drawer{module.drawers.length !== 1 ? 's' : ''}</span>
                  <span>&bull;</span>
                  <span>{calculateUsedUnits(module.drawers)}/3 units</span>
                  <span>&bull;</span>
                  <span>
                    {module.drawers.reduce((sum, d) => sum + d.items.length, 0)} item
                    {module.drawers.reduce((sum, d) => sum + d.items.length, 0) !== 1 ? 's' : ''}
                  </span>
                </div>

                {module.drawers.length > 0 && (
                  <div className="qconfig-module-overview-drawers">
                    {module.drawers.map((drawer) => (
                      <div key={drawer.id} className="qconfig-overview-drawer">
                        <div className="qconfig-overview-drawer-header">
                          <span className={`qconfig-size-badge ${drawer.size}`}>
                            {drawer.size.charAt(0).toUpperCase()}
                          </span>
                          <span className="qconfig-overview-drawer-name">{drawer.name}</span>
                          {drawer.image && <ImageIcon className="w-3 h-3 text-gray-400" />}
                        </div>
                        <div className="qconfig-overview-drawer-items">
                          {drawer.items.length === 0 ? (
                            <span className="empty">Empty</span>
                          ) : (
                            drawer.items.slice(0, 3).map((item, idx) => (
                              <span key={item.id} className="item-tag">
                                {item.name} {item.quantity > 1 && `(x${item.quantity})`}
                              </span>
                            ))
                          )}
                          {drawer.items.length > 3 && (
                            <span className="more">+{drawer.items.length - 3} more</span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default QConfiguratorSummary;
